import { motion } from "framer-motion";
import { useZoom } from "@rapid/contexts/ZoomContext";
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { AnnotationEditRequestDetails } from "@rapid/utils/annotationStrategies/types";
import { BoundingBox } from "@rapid/utils/annotationStrategies/BoundingBox";
import { getScaleFactors } from "@rapid/utils/scaleUtils";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFrame } from "@rapid/contexts/FrameContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import {
    useCurrentMedia,
    useCurrentMediaFileRenderedDimensions,
    useCurrentMediaFileNaturalDimensions,
    usePromptLabels
} from "@rapid/store";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { LABEL_TOP_PADDING } from "./colors";

function computeBboxTopLeftAnchorInContainerSpace(args: {
    annotation: BoundingBox;
    containerRect: DOMRect;
    canvasRect: DOMRect;
    naturalW: number;
    naturalH: number;
    baseRenderedW: number;
    baseRenderedH: number;
    zoomLevel: number;
    zoomX: number;
    zoomY: number;
}): { left: number; top: number; tlxBase: number; tlyBase: number } | null {
    const {
        annotation,
        containerRect,
        canvasRect,
        naturalW,
        naturalH,
        baseRenderedW,
        baseRenderedH,
        zoomLevel
    } = args;

    if (!baseRenderedW || !baseRenderedH || !naturalW || !naturalH) return null;

    const { scaleX, scaleY } = getScaleFactors({
        source: { width: naturalW, height: naturalH },
        target: { width: baseRenderedW, height: baseRenderedH }
    });

    // bbox top-left in base (unzoomed) rendered coords
    const tlxBase = (annotation.x - annotation.width / 2) * scaleX;
    const tlyBase = (annotation.y - annotation.height / 2) * scaleY;

    // Compute anchor from the *actual* post-transform canvas rect to match interactive area exactly.
    // Base canvas CSS size: width=baseRenderedW, height=baseRenderedH + LABEL_TOP_PADDING.
    const canvasStyleW = baseRenderedW;
    const canvasStyleH = baseRenderedH + LABEL_TOP_PADDING;

    // Apply offsets at default zoom that scale with zoom level
    const HORIZONTAL_OFFSET_AT_DEFAULT_ZOOM = -8;
    const VERTICAL_OFFSET_AT_DEFAULT_ZOOM = -1;
    const xInCanvasBase = tlxBase + HORIZONTAL_OFFSET_AT_DEFAULT_ZOOM;
    const yInCanvasBase = tlyBase + LABEL_TOP_PADDING + VERTICAL_OFFSET_AT_DEFAULT_ZOOM;

    const xInScreen = canvasRect.left + (xInCanvasBase / canvasStyleW) * canvasRect.width;
    const yInScreen = canvasRect.top + (yInCanvasBase / canvasStyleH) * canvasRect.height;

    const left = xInScreen - containerRect.left;
    const top = yInScreen - containerRect.top;

    return { left, top, tlxBase, tlyBase };
}

interface AnnotationLabelInputProps {
    annotationEditRequestDetails: AnnotationEditRequestDetails<BoundingBox>;
    containerRef: React.RefObject<HTMLDivElement>;
    onLabelChange: (annotationId: string, newLabel: string) => void;
    onPendingLabelChange?: (annotationId: string, newLabel: string) => void;
    onClose: (discardChanges?: boolean) => void;
    // canvasScale is removed as parent now handles full positioning and scaling if needed via style prop
}

const AnnotationLabelInput: React.FC<AnnotationLabelInputProps> = ({
    annotationEditRequestDetails,
    containerRef,
    onLabelChange,
    onPendingLabelChange,
    onClose
}) => {
    const currentLabel = annotationEditRequestDetails.annotation.label;
    const annotationId = annotationEditRequestDetails.annotation.id;

    const [label, setLabel] = useState(currentLabel);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [isOnCreateClass, setIsOnCreateClass] = useState(false);
    const [baseFilteredObjects, setBaseFilteredObjects] = useState<string[]>([]);
    const [originalInput, setOriginalInput] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const selectedItemRef = useRef<HTMLDivElement>(null);

    const currentMedia = useCurrentMedia();
    const currentMediaFileNaturalDimensions = useCurrentMediaFileNaturalDimensions();
    const currentMediaFileRenderedDimensions = useCurrentMediaFileRenderedDimensions();

    const { zoomLevel, x: zoomX, y: zoomY } = useZoom();
    const { deleteAnnotationFromCurrentFrame } = useFlow();
    const { setSelectedAnnotationId } = useFrame();
    const promptLabels = usePromptLabels();
    // const { triggerOnBoxDraw } = useAutoPredictions();
    const { track } = useAnalytics();
    const ADD_OBJECT_LABEL = "New object";

    // Get all unique object names from prompt labels
    const allObjects = useMemo(() => {
        return Array.from(promptLabels.keys()).sort();
    }, [promptLabels]);

    // Filter object names based on current input (only when user types)
    const filteredObjectNames = useMemo(() => {
        // Always show all object names if input is empty
        if (!label.trim()) return allObjects;

        // Use base filtered object names if user hasn't typed (navigating with arrows)
        if (!hasUserTyped) return baseFilteredObjects;

        const filtered = allObjects.filter((objectName) =>
            objectName.toLowerCase().startsWith(label.toLowerCase())
        );

        // Check if exact input matches any existing object name
        const exactMatch = allObjects.some(
            (objectName) => objectName.toLowerCase() === label.toLowerCase()
        );

        if (!exactMatch) {
            return [...filtered, ADD_OBJECT_LABEL];
        }

        return filtered;
    }, [allObjects, label, hasUserTyped, baseFilteredObjects]);

    useEffect(() => {
        if (allObjects.length > 0) {
            setShowSuggestions(allObjects.length > 1);
            setBaseFilteredObjects(allObjects);
            const currentIndex = allObjects.findIndex((objectName) => objectName === currentLabel);
            setSelectedSuggestionIndex(currentIndex >= 0 ? currentIndex : 0);
        }
    }, [allObjects, currentLabel]);

    // Auto-scroll to keep selected item visible
    useEffect(() => {
        if (selectedItemRef.current && suggestionsRef.current) {
            const selectedElement = selectedItemRef.current;
            const container = suggestionsRef.current;

            const containerRect = container.getBoundingClientRect();
            const selectedRect = selectedElement.getBoundingClientRect();

            // Check if the selected item is above the visible area
            if (selectedRect.top < containerRect.top) {
                selectedElement.scrollIntoView({ block: "start", behavior: "smooth" });
            }
            // Check if the selected item is below the visible area
            else if (selectedRect.bottom > containerRect.bottom) {
                selectedElement.scrollIntoView({ block: "end", behavior: "smooth" });
            }
        }
    }, [selectedSuggestionIndex, filteredObjectNames]);

    // Use useLayoutEffect for immediate focus after DOM updates
    useLayoutEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    // Fallback useEffect with delay in case useLayoutEffect doesn't work
    useEffect(() => {
        if (inputRef.current) {
            // Delay focus slightly to avoid race conditions with blur events on mount
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 10);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleSubmit = useCallback(
        (overrideLabel?: string) => {
            const labelToUse = overrideLabel || label;
            const trimmedLabel = labelToUse.trim();
            const labelChanged = trimmedLabel !== "" && trimmedLabel !== currentLabel;
            if (labelChanged) {
                onLabelChange(annotationId, trimmedLabel);
            }
            onClose();
        },
        [label, currentLabel, onLabelChange, annotationId, onClose]
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setLabel(newValue);
            setShowSuggestions(true);
            setHasUserTyped(true);
            setSelectedSuggestionIndex(0);
            setIsOnCreateClass(false);
            setOriginalInput(newValue);

            onPendingLabelChange?.(annotationId, newValue);

            if (newValue.trim()) {
                const filtered = allObjects.filter((objectName) =>
                    objectName.toLowerCase().startsWith(newValue.toLowerCase())
                );
                const exactMatch = allObjects.some(
                    (objectName) => objectName.toLowerCase() === newValue.toLowerCase()
                );
                if (!exactMatch) {
                    setBaseFilteredObjects([...filtered, ADD_OBJECT_LABEL]);
                } else {
                    setBaseFilteredObjects(filtered);
                }
            } else {
                setBaseFilteredObjects(allObjects);
            }
        },
        [allObjects, ADD_OBJECT_LABEL, annotationId, onPendingLabelChange]
    );

    const handleDeleteAnnotation = useCallback(() => {
        deleteAnnotationFromCurrentFrame(annotationId);
        setSelectedAnnotationId(null);
        onClose();
    }, [deleteAnnotationFromCurrentFrame, annotationId, setSelectedAnnotationId, onClose]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();

                const selectedSuggestion = filteredObjectNames[selectedSuggestionIndex];
                if (selectedSuggestion === ADD_OBJECT_LABEL) {
                    setShowSuggestions(false);
                    handleSubmit(originalInput);
                } else {
                    setLabel(selectedSuggestion);
                    setShowSuggestions(false);
                    handleSubmit(selectedSuggestion);
                }

                track({
                    event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                    data: { triggerMethod: "annotation_label_input_enter" }
                });
            } else if (e.key === "Escape") {
                onClose(true);
            } else if ((e.key === "Delete" || e.key === "Backspace") && !label) {
                e.preventDefault();
                handleDeleteAnnotation();
            } else if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
                e.preventDefault();
                if (showSuggestions && filteredObjectNames.length > 0) {
                    const newIndex =
                        selectedSuggestionIndex < filteredObjectNames.length - 1
                            ? selectedSuggestionIndex + 1
                            : 0;
                    setSelectedSuggestionIndex(newIndex);

                    // Update input to show selected suggestion, but don't trigger filtering
                    const selectedSuggestion = filteredObjectNames[newIndex];
                    const isNewObject = selectedSuggestion === ADD_OBJECT_LABEL;

                    if (isNewObject) {
                        // For add option, restore the original input
                        setLabel(originalInput);
                        setIsOnCreateClass(true);
                        onPendingLabelChange?.(annotationId, originalInput);
                    } else {
                        setLabel(selectedSuggestion);
                        setIsOnCreateClass(false);
                        onPendingLabelChange?.(annotationId, selectedSuggestion);
                    }
                    // Prevent filtering during navigation
                    setHasUserTyped(false);
                }
            } else if (
                e.key === "ArrowUp" ||
                (e.key === "Tab" && e.shiftKey) ||
                (e.keyCode === 9 && e.shiftKey)
            ) {
                e.preventDefault();
                if (showSuggestions && filteredObjectNames.length > 0) {
                    const newIndex =
                        selectedSuggestionIndex > 0
                            ? selectedSuggestionIndex - 1
                            : filteredObjectNames.length - 1;
                    setSelectedSuggestionIndex(newIndex);

                    const selectedSuggestion = filteredObjectNames[newIndex];
                    const isNewObject = selectedSuggestion === ADD_OBJECT_LABEL;

                    if (isNewObject) {
                        setLabel(originalInput);
                        setIsOnCreateClass(true);
                        onPendingLabelChange?.(annotationId, originalInput);
                    } else {
                        setLabel(selectedSuggestion);
                        setIsOnCreateClass(false);
                        onPendingLabelChange?.(annotationId, selectedSuggestion);
                    }
                    setHasUserTyped(false);
                }
            }
        },
        [
            filteredObjectNames,
            selectedSuggestionIndex,
            ADD_OBJECT_LABEL,
            originalInput,
            handleSubmit,
            onClose,
            showSuggestions,
            track,
            annotationId,
            onPendingLabelChange,
            handleDeleteAnnotation,
            label
        ]
    );

    const handleSuggestionClick = useCallback(
        (suggestion: string) => {
            if (suggestion === ADD_OBJECT_LABEL) {
                // For add option, submit the original input
                setShowSuggestions(false);
                handleSubmit(originalInput);
            } else {
                setLabel(suggestion);
                setShowSuggestions(false);
                handleSubmit(suggestion);
            }
        },
        [ADD_OBJECT_LABEL, handleSubmit, originalInput]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent) => {
            if (suggestionsRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }
            onClose();
        },
        [onClose]
    );

    // track the container size for positioning the labelInput ie. due to resizing
    const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
    useEffect(() => {
        const updateContainerRect = () => {
            if (containerRef.current) {
                setContainerRect(containerRef.current.getBoundingClientRect());
            }
        };

        // Initial measurement
        updateContainerRect();

        // Also listen for window resize as a fallback
        window.addEventListener("resize", updateContainerRect);

        return () => {
            window.removeEventListener("resize", updateContainerRect);
        };
    }, [containerRef]);

    const labelInputStyle = useMemo(() => {
        if (!(annotationEditRequestDetails && containerRef.current) || !containerRect) {
            return { display: "none" };
        }

        const canvasElement = containerRef.current.querySelector(
            "canvas[data-annotation-canvas]"
        ) as HTMLCanvasElement | null;
        if (!canvasElement) return { display: "none" };

        const canvasRect = canvasElement.getBoundingClientRect();

        const baseRenderedW = currentMediaFileRenderedDimensions?.width || 0;
        const baseRenderedH = currentMediaFileRenderedDimensions?.height || 0;
        const naturalW = currentMediaFileNaturalDimensions?.width || 0;
        const naturalH = currentMediaFileNaturalDimensions?.height || 0;
        if (!baseRenderedW || !baseRenderedH || !naturalW || !naturalH) return { display: "none" };

        const anno = annotationEditRequestDetails.annotation;
        const anchor = computeBboxTopLeftAnchorInContainerSpace({
            annotation: anno,
            containerRect,
            canvasRect,
            naturalW,
            naturalH,
            baseRenderedW,
            baseRenderedH,
            zoomLevel,
            zoomX,
            zoomY
        });
        if (!anchor) return { display: "none" };

        return {
            position: "absolute" as const,
            left: `${anchor.left}px`,
            top: `${anchor.top}px`,
            zIndex: 30
        };
    }, [
        annotationEditRequestDetails,
        containerRect,
        containerRef,
        currentMediaFileNaturalDimensions?.height,
        currentMediaFileNaturalDimensions?.width,
        currentMediaFileRenderedDimensions?.height,
        currentMediaFileRenderedDimensions?.width,
        zoomLevel,
        zoomX,
        zoomY
    ]);

    // Event handlers for inline functions
    const handleStopPropagation = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handlePreventDefault = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    const handleMouseEnter = useCallback(
        (index: number) => () => {
            setHoveredIndex(index);
        },
        []
    );

    const handleMouseLeave = useCallback(() => {
        setHoveredIndex(null);
    }, []);

    // Combine default styles with positioning styles from props
    const combinedStyle: React.CSSProperties = {
        // Default appearance
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        width: "auto",
        minWidth: "50px"
    };

    return (
        <motion.div
            key={`label-input-${annotationEditRequestDetails.annotation.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            style={{ ...labelInputStyle }}
        >
            <div
                className="relative"
                style={{
                    // Position the input just above the box's top-left corner
                    transform: "translateY(-100%)"
                }}
            >
                <div className="relative flex items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={label}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        style={combinedStyle}
                        onClick={handleStopPropagation}
                        onMouseDown={handleStopPropagation}
                        className="rounded-lg bg-primary/50 px-3 py-2 text-sm text-white ring-0 backdrop-blur-sm focus:outline-none focus:ring-1 focus:ring-purboflow-500"
                    />
                </div>

                {/* Suggestions dropdown */}
                {showSuggestions && filteredObjectNames.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        className="hideScrollbar absolute left-0 top-full z-40 mt-0.5 max-h-24 w-full min-w-32 overflow-y-auto rounded-lg border border-gray-600 bg-primary/50 shadow-lg backdrop-blur-sm"
                    >
                        {filteredObjectNames.slice(0, 10).map((objectName, index) => {
                            const isNewObject = objectName === ADD_OBJECT_LABEL;
                            return (
                                <div
                                    key={objectName}
                                    ref={index === selectedSuggestionIndex ? selectedItemRef : null}
                                    className={twClassNames(
                                        "relative flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm",
                                        isNewObject ? "font-medium text-white" : "text-white",
                                        index === selectedSuggestionIndex && "bg-primary/50",
                                        index === hoveredIndex &&
                                            index !== selectedSuggestionIndex &&
                                            "bg-gray-700/50"
                                    )}
                                    onClick={() => handleSuggestionClick(objectName)}
                                    onMouseDown={handlePreventDefault}
                                    onMouseEnter={handleMouseEnter(index)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    {isNewObject ? (
                                        <div className="flex items-center gap-2">
                                            <i className="fas fa-plus"></i>
                                            {objectName}
                                        </div>
                                    ) : (
                                        <span className="truncate">{objectName}</span>
                                    )}
                                    {index === selectedSuggestionIndex && (
                                        <span className="hotkey dark !border-opacity-50 !bg-opacity-50 font-normal backdrop-blur-sm">
                                            ⏎
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AnnotationLabelInput;
